import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, ViewChild, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { Table, TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { ToolbarModule } from 'primeng/toolbar';
import {
  ApiPaginationResult,
  PaginationResult,
} from '../../../../shared/models/api-result.model';
import { AddressService, StateOption } from '../../../../shared/services/address.service';
import { PaginationService } from '../../../../shared/services/pagination.service';
import { ContractTemplate } from '../../../models/contract-template.model';
import { ContractTemplateService } from '../../../services/contract-template.service';

@Component({
  selector: 'app-contract-template-list',
  standalone: true,
  templateUrl: './contract-template-list.component.html',
  imports: [
    CommonModule,
    FormsModule,
    SelectModule,
    TableModule,
    BreadcrumbModule,
    ToolbarModule,
    ButtonModule,
    RouterLink,
    ProgressSpinnerModule,
    ToastModule,
  ],
})
export class ContractTemplateListComponent implements OnDestroy {
  @ViewChild('table') table?: Table;

  private contractTemplateService = inject(ContractTemplateService);
  private addressService = inject(AddressService);
  private messageService = inject(MessageService);
  paginationService = inject(PaginationService);

  contractTemplates = signal<ContractTemplate[]>([]);
  countries = [{ value: 'US', label: 'Estados Unidos' }];
  states = this.addressService.getStates();

  pageNumber = signal(1);
  pageSize = signal(10);
  totalRecords = signal(0);
  loading = signal(false);

  countryFilter?: string;
  stateFilter?: string;

  private destroyFilter$ = new Subject<void>();
  private filterChanged$ = new Subject<void>();

  constructor() {
    this.filterChanged$
      .pipe(debounceTime(500), takeUntil(this.destroyFilter$))
      .subscribe(() => {
        this.pageNumber.set(0);
        this.table?.reset();
      });
  }

  ngOnDestroy(): void {
    this.destroyFilter$.next();
    this.destroyFilter$.complete();
  }

  onFilterChange(): void {
    this.filterChanged$.next();
  }

  loadData(event: any): void {
    const page = this.paginationService.mapLazyEventToPageRequest(
      event,
      this.pageSize(),
    );
    this.pageNumber.set(page.pageNumber);
    this.pageSize.set(page.pageSize);
    this.loading.set(true);

    this.contractTemplateService
      .getAll(
        this.pageNumber(),
        this.pageSize(),
        this.countryFilter,
        this.stateFilter,
      )
      .subscribe({
        next: (response: ApiPaginationResult) => {
          this.loading.set(false);

          const page = response.data as PaginationResult;
          this.contractTemplates.set(page.items as ContractTemplate[]);
          this.pageNumber.set(page.pageNumber);
          this.pageSize.set(page.pageSize);
          this.totalRecords.set(page.totalCount);
        },
        error: (e) => {
          this.loading.set(false);

          if (e instanceof HttpErrorResponse) {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao carregar os contratos',
              life: 3000,
            });
          }
        },
      });
  }

  getStateLabel(state: string): string {
    return this.states.find((option: StateOption) => option.value === state)
      ?.label ?? state;
  }

  getCountryLabel(country: string): string {
    const normalizedCountry = country?.toUpperCase();
    return (
      this.countries.find((option) => option.value === normalizedCountry)?.label ??
      country
    );
  }
}
